import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { fmtDateTime, fmtDuration, ACTION_LABELS } from './radiusUtils';

function Pager({ page, total, pageSize, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
      <span>Total {total} · Halaman {page}/{pages}</span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" className="h-8" disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return status === 'success'
    ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Accept</Badge>
    : <Badge className="text-[10px] bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-500/30"><XCircle className="w-3 h-3 mr-1" />Reject</Badge>;
}

export function AuthLogTab() {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(''); const [status, setStatus] = useState('all');
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const pageSize = 25;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (status !== 'all') params.result = status;
      if (from) params.date_from = from; if (to) params.date_to = to;
      const { data } = await api.get('/radius/logs', { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, search, status, from, to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, from, to]);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="relative w-full sm:w-56"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari username…" className="pl-8" data-testid="radius-log-search" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-36" data-testid="radius-log-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="accept">Access-Accept</SelectItem><SelectItem value="reject">Access-Reject</SelectItem></SelectContent></Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="radius-log-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="radius-log-to" />
      </div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Timestamp','Username','NAS','Status','Result'].map((h) => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">Tidak ada log authentication.</TableCell></TableRow>)}
              {!loading && items.map((x) => (
                <TableRow key={x.id}><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(x.timestamp)}</TableCell><TableCell className="text-sm font-mono">{x.username}</TableCell><TableCell className="text-xs">{x.nas}</TableCell><TableCell><StatusBadge status={x.status} /></TableCell><TableCell className="text-xs font-mono text-muted-foreground">{x.reply}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={total} pageSize={pageSize} onPage={setPage} />
      </CardContent></Card>
    </div>
  );
}

export function SessionsTab() {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(''); const [active, setActive] = useState('true');
  const pageSize = 25;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, active: active === 'true' };
      if (search) params.search = search;
      const { data } = await api.get('/radius/sessions', { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, search, active]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, active]);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="relative w-full sm:w-56"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari username…" className="pl-8" data-testid="radius-sess-search" /></div>
        <Select value={active} onValueChange={setActive}><SelectTrigger className="w-44" data-testid="radius-sess-active"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Active Sessions</SelectItem><SelectItem value="false">Semua Sessions</SelectItem></SelectContent></Select>
      </div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Username','NAS','NAS IP','Start','Last Update','Session Time','Client IP','Status'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">Tidak ada session.</TableCell></TableRow>)}
              {!loading && items.map((x) => (
                <TableRow key={x.id}><TableCell className="text-sm font-mono">{x.username}</TableCell><TableCell className="text-xs">{x.nas}</TableCell><TableCell className="text-xs font-mono">{x.nas_ip}</TableCell><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(x.start_time)}</TableCell><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(x.last_update)}</TableCell><TableCell className="text-xs tabular-nums">{fmtDuration(x.session_time)}</TableCell><TableCell className="text-xs font-mono">{x.client_ip || '—'}</TableCell><TableCell>{x.status === 'active' ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30">Active</Badge> : <Badge variant="outline" className="text-[10px]">Stopped</Badge>}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={total} pageSize={pageSize} onPage={setPage} />
      </CardContent></Card>
    </div>
  );
}

export function AuditLogTab() {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const pageSize = 25;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      const { data } = await api.get('/radius/audit-logs', { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div className="space-y-3">
      <div className="relative w-full sm:w-64"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari admin / target…" className="pl-8" data-testid="radius-audit-search" /></div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Timestamp','Admin','Action','Target','IP Address','Result'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">Belum ada audit log.</TableCell></TableRow>)}
              {!loading && items.map((x) => (
                <TableRow key={x.id}><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(x.timestamp)}</TableCell><TableCell className="text-xs">{x.admin || '—'}</TableCell><TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{ACTION_LABELS[x.action] || x.action}</Badge></TableCell><TableCell className="text-xs font-mono">{x.target || '—'}</TableCell><TableCell className="text-xs font-mono text-muted-foreground">{x.ip || '—'}</TableCell><TableCell>{x.result === 'success' ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30">success</Badge> : <Badge className="text-[10px] bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-500/30">{x.result}</Badge>}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={total} pageSize={pageSize} onPage={setPage} />
      </CardContent></Card>
    </div>
  );
}
